import type { FC } from 'react';
import React from 'react';
import { ButtonIcon, Icon } from 'venn-ui-kit';
import {
  type AnalysisSubjectType,
  logMessageToSentry,
  MANAGE_DATA_SECTION,
  navigateToManageDataPage,
  type PreviousLocation,
} from 'venn-utils';
import { useHistory } from 'react-router-dom';

export interface ManageInvestmentDataTriggerProps {
  fundId?: string;
  portfolioId?: number;
  subjectType?: AnalysisSubjectType;
  disabled?: boolean;
  customRenderer?: (onClick: () => void, disabled?: boolean) => React.ReactNode;
  /** Navigate to Returns/Price Data section when launching investment manage data page */
  defaultEditData?: boolean;
  /** Overwritable section to navigate to when launching manage data page */
  forceSection?: MANAGE_DATA_SECTION | null;
  needsAttention?: boolean;
  errorState?: boolean;
  location: PreviousLocation;
  tooltip?: React.ReactNode;
}

const ManageInvestmentDataTrigger: FC<React.PropsWithChildren<ManageInvestmentDataTriggerProps>> = ({
  defaultEditData,
  customRenderer,
  disabled,
  fundId,
  portfolioId,
  needsAttention,
  forceSection = null,
  errorState = false,
  location,
  tooltip,
}) => {
  const history = useHistory();
  const launchManageData = () => {
    if (!fundId && !portfolioId) {
      logMessageToSentry('Unable to navigate to manage data page due to missing fundId or portfolioId');
      return;
    }
    navigateToManageDataPage(
      history,
      { fundId, portfolioId },
      location,
      false,
      forceSection ?? (defaultEditData ? MANAGE_DATA_SECTION.DATA : MANAGE_DATA_SECTION.INVESTMENT_INFORMATION),
      {},
      errorState,
    );
  };

  const getTooltip = () => {
    if (tooltip) {
      return tooltip;
    }
    if (needsAttention) {
      return (
        <div>
          <h4>
            <strong>Manage data</strong>
          </h4>
          <div>
            <Icon type="exclamation-circle" />
            &nbsp; This investment is more than 6 months out of date or has an insufficient number of data points.
          </div>
        </div>
      );
    }
    return 'Manage data';
  };

  return customRenderer ? (
    customRenderer(launchManageData, disabled)
  ) : (
    <ButtonIcon
      className="qa-manage-data-button"
      iconType="cog"
      onClick={launchManageData}
      tooltip={getTooltip()}
      tooltipMaxWidth={needsAttention ? 300 : undefined}
      border
      disabled={disabled}
      destructive={needsAttention}
    />
  );
};

export default React.memo(ManageInvestmentDataTrigger);
